"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { customerApi, CustomerApiError } from "@/lib/customer-api";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";

/**
 * Bell + heart authed-state overlay rendered on deal-detail pages.
 *
 * Bug 6 fix (Jeanne 2026-05-19/20): paired with `DealActionButtonsShell`
 * which renders the anonymous-state buttons server-side. This Client
 * Component hydrates the customer's auth + reminder + favorite state,
 * and when authed, swaps out the shell via the wrapper's
 * `data-deal-actions-mode` attribute. When anonymous, returns null so
 * the shell remains the visible UI.
 *
 * Mount near the deal title (top of detail page) per Secret Escapes /
 * Booking.com pattern. State is hydrated from a single /customer/me
 * + parallel /customer/reminders + /customer/favorites call on mount.
 */
export default function DealActionButtons({ dealId, dealTitle, dealImageUrl }) {
  const router = useRouter();
  const pathname = usePathname();
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  const [authed, setAuthed] = useState(false);
  const [reminder, setReminder] = useState(null); // {id} | null
  const [favorite, setFavorite] = useState(null); // {id} | null
  const [pendingBell, setPendingBell] = useState(false);
  const [pendingHeart, setPendingHeart] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    const controller = new AbortController();
    (async () => {
      try {
        // meQuiet (E2E QA 2026-06-10): no network call for anonymous
        // visitors (hint-gated), shared single-flight when authed.
        const profile = await customerApi.meQuiet();
        if (!profile) return; // anonymous: buttons act as login prompts
        if (controller.signal.aborted) return;
        const [rem, fav] = await Promise.all([
          customerApi.reminders({ signal: controller.signal }),
          customerApi.favorites({ signal: controller.signal }),
        ]);
        if (controller.signal.aborted) return;
        const dealIdInt = Number(dealId);
        const matchedReminder = rem.reminders.find(
          (r) => r.dealId === dealIdInt,
        );
        const matchedFavorite = fav.deals.find(
          (f) => Number(f.slug) === dealIdInt,
        );
        setAuthed(true);
        setReminder(matchedReminder ? { id: matchedReminder.id } : null);
        setFavorite(matchedFavorite ? { id: matchedFavorite.id } : null);
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err?.name === "AbortError") return;
        // 401 = anonymous, leave authed=false. Other errors: silent;
        // buttons still work as login-prompts.
        if (err instanceof CustomerApiError && err.status === 401) {
          setAuthed(false);
        }
      }
    })();
    return () => controller.abort();
  }, [dealId]);

  // Toggle the wrapping container's data attribute so CSS hides the
  // anonymous shell when this overlay is visible.
  useEffect(() => {
    const containers = document.querySelectorAll(
      "[data-deal-actions-wrap]",
    );
    containers.forEach((el) => {
      el.setAttribute("data-deal-actions-mode", authed ? "authed" : "anon");
    });
  }, [authed]);

  function bounceToLogin() {
    const returnTo = encodeURIComponent(pathname || "/");
    router.push(`/account/login?returnTo=${returnTo}`);
  }

  async function toggleBell() {
    if (pendingBell) return;
    if (!authed) return bounceToLogin();
    setPendingBell(true);
    try {
      if (reminder) {
        await customerApi.deleteReminder(reminder.id);
        setReminder(null);
      } else {
        const created = await customerApi.createReminder({
          dealId: Number(dealId),
          triggerType: "both",
        });
        setReminder({ id: created.id });
      }
    } catch {
      // Soft fail; user can retry.
    } finally {
      setPendingBell(false);
    }
  }

  async function toggleHeart() {
    if (pendingHeart) return;
    if (!authed) return bounceToLogin();
    setPendingHeart(true);
    try {
      if (favorite) {
        await customerApi.deleteFavorite(favorite.id);
        setFavorite(null);
      } else {
        const created = await customerApi.createFavorite({
          kind: "deal",
          slug: String(dealId),
          displayName: dealTitle || `Deal ${dealId}`,
        });
        setFavorite({ id: created.id });
      }
    } catch {
      // Soft fail.
    } finally {
      setPendingHeart(false);
    }
  }

  // Render nothing when anonymous — let the SSR shell handle the
  // visible UI (Bug 6 SSR-shell + Client-swap pattern, Jeanne 2026-05-20).
  if (!authed) return null;

  // Authed: render the interactive bell + heart with reminder/favorite state.
  return (
    <div className="deal-actions-auth flex items-center gap-2" data-deal-actions-state="authed">
      <button
        type="button"
        onClick={toggleBell}
        disabled={pendingBell}
        aria-pressed={!!reminder}
        aria-label={
          reminder
            ? t("deal.action_remind_off_aria", "Herinnering uitzetten voor deze deal")
            : t("deal.action_remind_aria", "Stel een prijs- of beschikbaarheidsmelding in")
        }
        title={
          reminder
            ? t("deal.action_remind_active_title", "Herinnering actief, klik om uit te schakelen")
            : t("deal.action_remind_title", "Stel een melding in bij prijsdaling of beschikbaarheid")
        }
        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
          reminder
            ? "border-[var(--color-orange)] bg-[var(--color-orange)]/10 text-[var(--color-orange-dark)] hover:bg-[var(--color-orange)]/20"
            : "border-[var(--color-border)] bg-white text-[var(--color-text-primary)] hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill={reminder ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
        <span className="hidden sm:inline">
          {reminder ? t("deal.action_reminding", "Herinnering aan") : t("deal.action_remind", "Herinner mij")}
        </span>
      </button>

      <button
        type="button"
        onClick={toggleHeart}
        disabled={pendingHeart}
        aria-pressed={!!favorite}
        aria-label={
          favorite
            ? t("deal.action_save_off_aria", "Verwijder uit favorieten")
            : t("deal.action_save_aria", "Voeg toe aan favorieten")
        }
        title={
          favorite
            ? t("deal.action_saved_title", "Opgeslagen in je favorieten, klik om te verwijderen")
            : t("deal.action_save_title", "Bewaar in je favorieten")
        }
        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
          favorite
            ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
            : "border-[var(--color-border)] bg-white text-[var(--color-text-primary)] hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill={favorite ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
        </svg>
        <span className="hidden sm:inline">
          {favorite ? t("deal.action_saved", "Opgeslagen") : t("deal.action_save", "Bewaar")}
        </span>
      </button>
    </div>
  );
}
